import { useMemo, useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useShopVertical } from "../lib/useShopVertical";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Search, Loader2, Plus, FlaskConical, ShieldAlert, Lightbulb, Trash2,
  ExternalLink, Sparkles, Layers, ImageIcon, X, Clock, Rocket,
} from "lucide-react";
import { apiClient } from "../lib/api";
import { useDesignModel, modelLabel } from "../lib/useDesignModel";
import { fidelityOptionsFor, useFidelity } from "../lib/designPrompt";
import type { Analysis, ErankListing } from "@erc/shared";
import { CustomAutoModal, CustomAutoQueue } from "../components/analysis/CustomAutopilot";

// Analizdeki Etsy API ozeti + eklentinin hesapladigi potansiyel (arama karti rozetiyle ayni)
type EtsyStats = { views?: number | null; favorites?: number; ageDays?: number; viewsPerDay?: number | null; favPerDay?: number; potential?: number };

const CP_BADGE: Record<string, { label: string; cls: string }> = {
  low: { label: "Düşük", cls: "bg-green-100 text-green-700" },
  medium: { label: "Orta", cls: "bg-amber-100 text-amber-700" },
  high: { label: "Yüksek", cls: "bg-red-100 text-red-700" },
};

type SortKey = "newest" | "oldest" | "listings" | "designs";

const FILTERS_KEY = "erc:analyses:filters";
type SavedFilters = {
  category: string; copyright: string; sort: SortKey; onlyNoListing: boolean; onlyNoDesign: boolean;
  customFilter: "all" | "custom" | "not"; erankRanges: Record<string, { min: string; max: string }>;
};

// Kompakt sayfa numaralari: 1 … 4 5 [6] 7 8 … 20
function pageNumbers(current: number, total: number): (number | "…")[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const out: (number | "…")[] = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) out.push("…");
  for (let p = start; p <= end; p++) out.push(p);
  if (end < total - 1) out.push("…");
  out.push(total);
  return out;
}

export default function ProductAnalysesPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  // Son secilen filtreler tarayicida hatirlanir (arama metni haric)
  const [saved] = useState<Partial<SavedFilters>>(() => {
    try { return JSON.parse(localStorage.getItem(FILTERS_KEY) || "{}"); } catch { return {}; }
  });
  const [category, setCategory] = useState(saved.category ?? "");
  const [copyright, setCopyright] = useState(saved.copyright ?? "");
  const [sort, setSort] = useState<SortKey>(saved.sort ?? "newest");
  const [onlyNoListing, setOnlyNoListing] = useState(saved.onlyNoListing ?? false);
  const [onlyNoDesign, setOnlyNoDesign] = useState(saved.onlyNoDesign ?? false);
  const [customFilter, setCustomFilter] = useState<"all" | "custom" | "not">(saved.customFilter ?? "all");
  const [erankRanges, setErankRanges] = useState<Record<string, { min: string; max: string }>>(saved.erankRanges ?? {});
  useEffect(() => {
    const f: SavedFilters = { category, copyright, sort, onlyNoListing, onlyNoDesign, customFilter, erankRanges };
    try { localStorage.setItem(FILTERS_KEY, JSON.stringify(f)); } catch { /* depolama kapali */ }
  }, [category, copyright, sort, onlyNoListing, onlyNoDesign, customFilter, erankRanges]);
  const setER = (k: string, w: "min" | "max", v: string) =>
    setErankRanges((p) => ({ ...p, [k]: { ...(p[k] ?? { min: "", max: "" }), [w]: v } }));
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showAutoModal, setShowAutoModal] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  // Sayfa basina satir: 10/20/50/100 (varsayilan 20), tarayicida hatirlanir
  const [PAGE_SIZE, setPageSize] = useState<number>(() => {
    try { const v = Number(localStorage.getItem("erc:analyses:page-size")); return [10, 20, 50, 100].includes(v) ? v : 20; } catch { return 20; }
  });
  const changePageSize = (n: number) => {
    setPageSize(n); setPage(1);
    try { localStorage.setItem("erc:analyses:page-size", String(n)); } catch { /* depolama kapali */ }
  };

  const { data: analyses = [], isLoading } = useQuery({
    queryKey: ["analyses"],
    queryFn: () => apiClient.listAnalyses(),
    // Uretim suren tasarim varsa satir durumlari tazelensin
    refetchInterval: (q) => ((q.state.data as Analysis[] | undefined)?.some((a) => a.designStats?.generating) ? 10_000 : false),
  });

  const deleteM = useMutation({
    mutationFn: (id: string) => apiClient.deleteAnalysis(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["analyses"] }),
  });

  const bulkDeleteM = useMutation({
    mutationFn: async (ids: string[]) => { await Promise.all(ids.map((id) => apiClient.deleteAnalysis(id))); },
    onSuccess: (_r, ids) => {
      queryClient.invalidateQueries({ queryKey: ["analyses"] });
      setSelected(new Set());
      setToast(`${ids.length} analiz silindi.`);
      setTimeout(() => setToast(null), 5000);
    },
  });

  const customM = useMutation({
    mutationFn: ({ id, custom }: { id: string; custom: boolean }) => apiClient.setAnalysisCustom(id, custom),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["analyses"] }),
  });

  const bulkCustomM = useMutation({
    mutationFn: async ({ ids, custom }: { ids: string[]; custom: boolean }) => {
      await Promise.all(ids.map((id) => apiClient.setAnalysisCustom(id, custom)));
    },
    onSuccess: (_r, { ids, custom }) => {
      queryClient.invalidateQueries({ queryKey: ["analyses"] });
      setSelected(new Set());
      setToast(`${ids.length} analiz ${custom ? "Custom işaretlendi" : "Custom kaldırıldı"}.`);
      setTimeout(() => setToast(null), 5000);
    },
  });

  // Aynı listelemeyi tekrar gösterme: kaynak URL'e göre en yeniyi tut
  const deduped = useMemo(() => {
    const seen = new Set<string>();
    const out: Analysis[] = [];
    for (const a of analyses) {
      const key = (a.source?.url || a.id).toLowerCase().replace(/[?#].*$/, "");
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(a);
    }
    return out;
  }, [analyses]);

  const categories = useMemo(
    () => Array.from(new Set(deduped.map((a) => a.productType).filter(Boolean))).sort(),
    [deduped],
  );

  // eRank verisi: analizlerin listing ID'lerinden (yuklenmis CSV varsa) metrikler
  const erankListingIds = useMemo(() => {
    const ids = new Set<string>();
    for (const a of deduped) { const m = a.source?.url?.match(/listing\/(\d+)/); if (m) ids.add(m[1]); }
    return Array.from(ids);
  }, [deduped]);
  const { data: erankMap = {} } = useQuery({
    queryKey: ["erank-by-listings", erankListingIds],
    queryFn: () => apiClient.getErankByListings(erankListingIds),
    enabled: erankListingIds.length > 0,
  });
  const erankOf = (a: Analysis): ErankListing | undefined => {
    const m = a.source?.url?.match(/listing\/(\d+)/);
    return m ? (erankMap as Record<string, ErankListing>)[m[1]] : undefined;
  };
  const ER_KEYS = ["totalViews", "favorites", "estSales", "listingAge"] as const;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = deduped.filter((a: Analysis) => {
      if (category && a.productType !== category) return false;
      if (copyright && (a.copyright?.level ?? "") !== copyright) return false;
      if (onlyNoListing && (a._count?.listingDrafts ?? 0) > 0) return false;
      if (onlyNoDesign && (a._count?.designs ?? 0) > 0) return false;
      if (customFilter !== "all") {
        const isC = (a.source?.tags ?? []).some((t) => String(t).toLowerCase() === "custom");
        if (customFilter === "custom" && !isC) return false;
        if (customFilter === "not" && isC) return false;
      }
      // eRank metrik filtreleri (min/max) — filtre aktifse eRank verisi gerekir
      for (const key of ER_KEYS) {
        const r = erankRanges[key];
        if (!r || (!r.min && !r.max)) continue;
        const e = erankOf(a);
        const val = e ? (e[key] as number) : undefined;
        if (val === undefined) return false;
        if (r.min !== "" && val < Number(r.min)) return false;
        if (r.max !== "" && val > Number(r.max)) return false;
      }
      if (!q) return true;
      return [a.source?.title, a.niche, a.productType, a.suggestedDescription, ...(a.suggestedTags ?? []), ...(a.source?.tags ?? [])]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
    const cnt = (a: Analysis, k: "listingDrafts" | "designs") => a._count?.[k] ?? 0;
    list.sort((a, b) => {
      switch (sort) {
        case "oldest": return +new Date(a.createdAt) - +new Date(b.createdAt);
        case "listings": return cnt(b, "listingDrafts") - cnt(a, "listingDrafts");
        case "designs": return cnt(b, "designs") - cnt(a, "designs");
        default: return +new Date(b.createdAt) - +new Date(a.createdAt);
      }
    });
    return list;
  }, [deduped, search, category, copyright, sort, onlyNoListing, onlyNoDesign, customFilter, erankRanges, erankMap]);

  // Sayfalama
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const paged = useMemo(
    () => filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [filtered, safePage, PAGE_SIZE]
  );
  // Filtre/arama/sıralama değişince ilk sayfaya dön
  useEffect(() => { setPage(1); }, [search, category, copyright, sort, onlyNoListing, onlyNoDesign, customFilter, erankRanges]);

  // "Tümünü seç" görünen sayfadaki satırlar üzerinde çalışır
  const allSelected = paged.length > 0 && paged.every((a) => selected.has(a.id));
  function toggleAll() {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) paged.forEach((a) => next.delete(a.id));
      else paged.forEach((a) => next.add(a.id));
      return next;
    });
  }
  function toggleOne(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function confirmDelete(a: Analysis) {
    if (confirm(`"${a.source?.title ?? a.productType}" analizi silinsin mi?`)) {
      deleteM.mutate(a.id);
      setSelected((prev) => { const n = new Set(prev); n.delete(a.id); return n; });
    }
  }

  return (
    <div className="mx-auto max-w-6xl px-6 py-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-bold text-gray-900">
            <FlaskConical className="h-6 w-6 text-indigo-600" /> Ürün Analizleri
          </h1>
          <p className="mt-0.5 text-sm text-gray-500">
            Eklentiden veya panelden yaptığın tüm ürün analizleri. Bir analize tıklayarak detay, rakip etiketleri ve brief'leri gör.
          </p>
        </div>
        <button
          onClick={() => navigate("/analyses/new")}
          className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
        >
          <Plus className="h-4 w-4" /> Yeni Analiz
        </button>
      </div>

      {/* Filtreler */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Başlık, niş veya etiket ara…"
            className="w-full rounded-lg border border-gray-200 py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
          />
        </div>
        {categories.length > 0 && (
          <select value={category} onChange={(e) => setCategory(e.target.value)}
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200">
            <option value="">Tüm kategoriler</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
        <select value={copyright} onChange={(e) => setCopyright(e.target.value)}
          className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200">
          <option value="">Tüm telif</option>
          <option value="low">Telif: Düşük</option>
          <option value="medium">Telif: Orta</option>
          <option value="high">Telif: Yüksek</option>
        </select>
        <select value={sort} onChange={(e) => setSort(e.target.value as SortKey)}
          className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200">
          <option value="newest">En yeni</option>
          <option value="oldest">En eski</option>
          <option value="listings">En çok bağlı liste</option>
          <option value="designs">En çok tasarım</option>
        </select>
        <button onClick={() => setOnlyNoListing((v) => !v)}
          title="Sadece hiç listing oluşturulmamış analizleri göster"
          className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${onlyNoListing ? "border-indigo-500 bg-indigo-50 text-indigo-700" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}>
          <Layers className="h-3.5 w-3.5" /> Listesi olmayanlar
        </button>
        <button onClick={() => setOnlyNoDesign((v) => !v)}
          title="Sadece hiç tasarım üretilmemiş analizleri göster"
          className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${onlyNoDesign ? "border-indigo-500 bg-indigo-50 text-indigo-700" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}>
          <ImageIcon className="h-3.5 w-3.5" /> Tasarımı olmayanlar
        </button>
        <button onClick={() => setCustomFilter((v) => (v === "all" ? "custom" : v === "custom" ? "not" : "all"))}
          title="Custom filtresi: Tümü → Custom → Custom değil"
          className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${customFilter === "custom" ? "border-pink-500 bg-pink-50 text-pink-700" : customFilter === "not" ? "border-gray-400 bg-gray-100 text-gray-700" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}>
          Custom: {customFilter === "custom" ? "Custom" : customFilter === "not" ? "Custom değil" : "Tümü"}
        </button>
        {(category || copyright || onlyNoListing || onlyNoDesign || customFilter !== "all" || Object.keys(erankRanges).length > 0) && (
          <button onClick={() => { setCategory(""); setCopyright(""); setOnlyNoListing(false); setOnlyNoDesign(false); setCustomFilter("all"); setErankRanges({}); }} className="text-xs text-gray-400 hover:text-gray-600">Filtreleri temizle</button>
        )}
      </div>

      {/* eRank metrik filtreleri (yuklenmis CSV varsa) */}
      <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
        {([
          { key: "totalViews", label: "Görüntülenme" },
          { key: "favorites", label: "Favori" },
          { key: "estSales", label: "Tah. Satış" },
          { key: "listingAge", label: "Liste yaşı (gün)" },
        ] as const).map((m) => (
          <div key={m.key} className="rounded-lg border border-gray-100 bg-gray-50/60 p-2">
            <div className="mb-1 text-[10px] font-semibold text-gray-500">eRank · {m.label}</div>
            <div className="flex items-center gap-1">
              <input type="number" placeholder="min" value={erankRanges[m.key]?.min ?? ""}
                onChange={(e) => setER(m.key, "min", e.target.value)}
                className="w-full rounded border border-gray-200 px-1.5 py-1 text-[11px]" />
              <span className="text-gray-300">–</span>
              <input type="number" placeholder="max" value={erankRanges[m.key]?.max ?? ""}
                onChange={(e) => setER(m.key, "max", e.target.value)}
                className="w-full rounded border border-gray-200 px-1.5 py-1 text-[11px]" />
            </div>
          </div>
        ))}
      </div>

      <CustomAutoQueue />

      {/* Toplu islem cubugu */}
      {selected.size > 0 && (
        <div className="mb-3 flex items-center gap-3 rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-2.5">
          <span className="text-sm font-medium text-indigo-800">{selected.size} analiz seçildi</span>
          <button onClick={() => setShowBulkModal(true)}
            className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700">
            <Sparkles className="h-3.5 w-3.5" /> Tasarım Oluştur ({selected.size})
          </button>
          <button onClick={() => bulkCustomM.mutate({ ids: Array.from(selected), custom: true })} disabled={bulkCustomM.isPending}
            className="flex items-center gap-1.5 rounded-lg bg-pink-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-pink-600 disabled:opacity-50">
            {bulkCustomM.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Custom işaretle
          </button>
          <button onClick={() => setShowAutoModal(true)} title="Custom analizlerden tasarım → isim ailesi → kolaj → mockup → taslak, sunucuda otomatik"
            className="flex items-center gap-1.5 rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-violet-700">
            <Rocket className="h-3.5 w-3.5" /> Otomatik Custom Liste ({selected.size})
          </button>
          <button onClick={() => bulkCustomM.mutate({ ids: Array.from(selected), custom: false })} disabled={bulkCustomM.isPending}
            className="flex items-center gap-1.5 rounded-lg border border-pink-300 bg-white px-3 py-1.5 text-xs font-medium text-pink-600 hover:bg-pink-50 disabled:opacity-50">
            Custom kaldır
          </button>
          <button
            onClick={() => { if (confirm(`${selected.size} analiz kalıcı olarak silinsin mi?`)) bulkDeleteM.mutate(Array.from(selected)); }}
            disabled={bulkDeleteM.isPending}
            className="flex items-center gap-1.5 rounded-lg bg-red-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-600 disabled:opacity-50">
            {bulkDeleteM.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />} Sil ({selected.size})
          </button>
          <button onClick={() => setSelected(new Set())} className="ml-auto text-xs text-indigo-500 hover:text-indigo-700">Seçimi temizle</button>
        </div>
      )}

      {/* Liste */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20 text-gray-400">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Yükleniyor…
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-200 py-16 text-center">
          <FlaskConical className="mx-auto mb-3 h-10 w-10 text-gray-300" />
          <p className="text-sm text-gray-500">
            {deduped.length > 0 ? "Filtreye uyan analiz yok. Filtreleri temizlemeyi dene." : "Henüz analiz yok. Eklentiden bir ürünü analiz et veya yeni analiz başlat."}
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          {/* Baslik satiri */}
          <div className="flex items-center gap-3 border-b border-gray-100 bg-gray-50 px-4 py-2">
            <input type="checkbox" checked={allSelected} onChange={toggleAll}
              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-200" />
            <span className="text-xs text-gray-400">
              {filtered.length} analiz{category ? ` · ${category}` : ""}
              {pageCount > 1 && ` · ${(safePage - 1) * PAGE_SIZE + 1}–${Math.min(safePage * PAGE_SIZE, filtered.length)} arası (sayfa ${safePage}/${pageCount})`}
            </span>
            <label className="ml-auto flex items-center gap-1.5 text-xs text-gray-500">
              Satır
              <select value={PAGE_SIZE} onChange={(e) => changePageSize(Number(e.target.value))}
                className="rounded border border-gray-200 bg-white px-1.5 py-0.5 text-xs text-gray-700">
                {[10, 20, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
          </div>
          <div className="divide-y divide-gray-100">
            {paged.map((a) => (
              <AnalysisRow
                key={a.id}
                a={a}
                selected={selected.has(a.id)}
                selecting={selected.size > 0}
                onToggle={() => toggleOne(a.id)}
                onDelete={() => confirmDelete(a)}
                erank={erankOf(a)}
                onToggleCustom={() => customM.mutate({ id: a.id, custom: !(a.source?.tags ?? []).some((t) => String(t).toLowerCase() === "custom") })}
              />
            ))}
          </div>
          {pageCount > 1 && (
            <div className="flex items-center justify-between gap-2 border-t border-gray-100 bg-gray-50 px-4 py-2.5">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage <= 1}
                className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed">
                ← Önceki
              </button>
              <div className="flex items-center gap-1">
                {pageNumbers(safePage, pageCount).map((p, i) =>
                  p === "…" ? (
                    <span key={`e${i}`} className="px-1.5 text-xs text-gray-400">…</span>
                  ) : (
                    <button key={p} onClick={() => setPage(p as number)}
                      className={`min-w-[28px] rounded-lg px-2 py-1 text-xs font-medium ${p === safePage ? "bg-indigo-600 text-white" : "text-gray-600 hover:bg-white"}`}>
                      {p}
                    </button>
                  )
                )}
              </div>
              <button onClick={() => setPage((p) => Math.min(pageCount, p + 1))} disabled={safePage >= pageCount}
                className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed">
                Sonraki →
              </button>
            </div>
          )}
        </div>
      )}

      {showBulkModal && (
        <BulkDesignModal
          count={selected.size}
          onClose={() => setShowBulkModal(false)}
          onDone={(msg) => {
            setShowBulkModal(false);
            setSelected(new Set());
            setToast(msg);
            setTimeout(() => setToast(null), 8000);
            queryClient.invalidateQueries({ queryKey: ["analyses"] });
          }}
          analysisIds={Array.from(selected)}
        />
      )}

      {showAutoModal && (
        <CustomAutoModal
          analysisIds={Array.from(selected)}
          onClose={() => setShowAutoModal(false)}
          onDone={(msg) => {
            setShowAutoModal(false);
            setSelected(new Set());
            setToast(msg);
            setTimeout(() => setToast(null), 8000);
          }}
        />
      )}

      {toast && (
        <div className="fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-lg bg-gray-900 px-4 py-3 text-sm text-white shadow-lg">
          <Sparkles className="h-4 w-4 text-indigo-300" /> {toast}
        </div>
      )}
    </div>
  );
}

// ==== Satir ====
function AnalysisRow({ a, selected, selecting, onToggle, onDelete, onToggleCustom, erank }: {
  a: Analysis; selected: boolean;
  /** En az bir satir seciliyse: satira tiklamak secer (baglanti acilmaz; Ctrl/Cmd+tik acar) */
  selecting: boolean;
  onToggle: () => void; onDelete: () => void; onToggleCustom: () => void; erank?: ErankListing;
}) {
  const thumb = a.source?.imageUrls?.[0];
  const cp = a.copyright ? (CP_BADGE[a.copyright.level] ?? CP_BADGE.low) : null;
  const briefCount = a.ideaBriefs?.length ?? 0;
  const listingCount = a._count?.listingDrafts ?? 0;
  const designCount = a._count?.designs ?? 0;
  const gen = a.designStats?.generating ?? 0;
  const failed = a.designStats?.failed ?? 0;
  const etsyId = a.source?.url?.match(/listing\/(\d+)/)?.[1];
  const custom = (a.source?.tags ?? []).some((t) => String(t).toLowerCase() === "custom");
  const demand = (a.source?.rawData as { demand?: { inCarts?: number; recentPurchases?: number; views24?: number; salesCount?: string; etsyStats?: EtsyStats } } | null)?.demand;

  return (
    <div
      onClickCapture={(e) => {
        if (!selecting || e.ctrlKey || e.metaKey) return;
        // Satirdaki dugmeler (Custom, sil...) ve kutu kendi isini yapsin
        if ((e.target as HTMLElement).closest("button, input, select, textarea, label")) return;
        e.preventDefault(); e.stopPropagation();
        onToggle();
      }}
      className={`flex items-start gap-3 px-4 py-3 transition-colors hover:bg-indigo-50/40 ${selected ? "bg-indigo-50/60" : ""} ${selecting ? "cursor-pointer select-none" : ""}`}>
      {/* Genis tiklama alani: kutunun cevresi de secer */}
      <label onClick={(e) => e.stopPropagation()} className="-my-3 -ml-4 flex cursor-pointer self-stretch py-3 pl-4 pr-2">
        <input type="checkbox" checked={selected} onChange={onToggle}
          className="mt-[46px] h-5 w-5 cursor-pointer rounded border-gray-300 text-indigo-600 focus:ring-indigo-200 sm:mt-[70px]" />
      </label>

      <Link to={`/analyses/${a.id}`} className="h-28 w-28 flex-shrink-0 overflow-hidden rounded-lg border border-gray-200 bg-gray-100 sm:h-40 sm:w-40">
        {thumb ? <img src={thumb} alt="" loading="lazy" className="h-full w-full object-cover" />
          : <span className="flex h-full w-full items-center justify-center"><ImageIcon className="h-6 w-6 text-gray-300" /></span>}
      </Link>

      <div className="min-w-0 flex-1">
        <Link to={`/analyses/${a.id}`} className="block text-left">
          <h3 className="font-semibold text-gray-900 line-clamp-2 hover:text-indigo-600">{a.source?.title ?? a.productType}</h3>
        </Link>
        <p className="mt-0.5 text-sm text-gray-500">{a.niche}</p>
        {a.suggestedDescription && (
          <p className="mt-1 text-xs text-gray-500 line-clamp-2 leading-snug">{a.suggestedDescription}</p>
        )}
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <span className="inline-flex items-center rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-700">{a.productType}</span>
          <button
            onClick={onToggleCustom}
            title={custom ? "Custom etiketini kaldır" : "Custom olarak işaretle"}
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold transition-colors ${custom ? "bg-pink-100 text-pink-700 hover:bg-pink-200" : "border border-dashed border-gray-300 text-gray-400 hover:border-pink-300 hover:text-pink-500"}`}>
            {custom ? "Custom ✕" : "+ Custom"}
          </button>
          {cp && (
            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${cp.cls}`}>
              <ShieldAlert className="h-3 w-3" /> Telif: {cp.label}
            </span>
          )}
          {demand?.recentPurchases != null && (
            <span className="inline-flex items-center rounded-full bg-orange-100 px-2 py-0.5 text-[11px] font-semibold text-orange-700" title="Son 24 saatte alan kişi sayısı">🔥 {demand.recentPurchases} (24s)</span>
          )}
          {demand?.views24 != null && (
            <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700" title="Son 24 saatte görüntülenme">👁 {demand.views24} (24s)</span>
          )}
          {demand?.etsyStats?.potential != null && (
            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${demand.etsyStats.potential >= 70 ? "bg-emerald-50 text-emerald-700" : demand.etsyStats.potential >= 45 ? "bg-amber-50 text-amber-700" : "bg-gray-100 text-gray-600"}`}
              title={`Potansiyel (analiz anı)${demand.etsyStats.ageDays != null ? ` · ${demand.etsyStats.ageDays} günlük liste` : ""}${demand.etsyStats.favorites != null ? ` · ${demand.etsyStats.favorites} favori` : ""}`}>
              Potansiyel {demand.etsyStats.potential}
            </span>
          )}
          {demand?.inCarts != null && (
            <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700" title="Sepette">🛒 {demand.inCarts} sepet</span>
          )}
          {etsyId && (
            <a href={`https://www.etsy.com/listing/${etsyId}`} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-0.5 text-[11px] font-medium text-orange-600 hover:underline">
              <ExternalLink className="h-3 w-3" /> Etsy'de Aç
            </a>
          )}
        </div>
        {erank && (
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md bg-emerald-50/70 px-2 py-1 text-[11px] text-gray-600">
            <span className="font-semibold text-emerald-700">eRank</span>
            <span title="Görüntülenme">👁 {erank.totalViews.toLocaleString("tr-TR")}</span>
            <span title="Günlük görüntülenme">⚡ {erank.dailyViews.toLocaleString("tr-TR", { maximumFractionDigits: 1 })}/gün</span>
            <span title="Favori">❤ {erank.favorites.toLocaleString("tr-TR")}</span>
            <span title="Tah. satış" className="font-semibold text-emerald-700">🛒 {erank.estSales.toLocaleString("tr-TR")} satış</span>
            <span title="Tah. ciro" className="text-emerald-700">${erank.estRevenue.toLocaleString("en-US", { maximumFractionDigits: 0 })}</span>
            <span title="Fiyat">${erank.price.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            <span title="Liste yaşı">⏳ {erank.listingAge}g</span>
          </div>
        )}
      </div>

      {/* Sag: istatistikler + tarih */}
      <div className="flex flex-shrink-0 flex-col items-end gap-1.5 text-xs text-gray-500">
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-1" title="Brief fikri"><Lightbulb className="h-3.5 w-3.5 text-indigo-400" />{briefCount}</span>
          <span className="inline-flex items-center gap-1" title="Bağlı listing"><Layers className="h-3.5 w-3.5 text-emerald-400" />{listingCount}</span>
          <span className="inline-flex items-center gap-1" title="Üretilen tasarım"><ImageIcon className="h-3.5 w-3.5 text-purple-400" />{designCount}</span>
        </div>
        {gen > 0 && (
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700" title="Arka planda üretilen tasarım">
            <Loader2 className="h-3 w-3 animate-spin" /> {gen} üretiliyor
          </span>
        )}
        {failed > 0 && (
          <Link to={`/analyses/${a.id}`} className="rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-600 hover:bg-red-100" title="Üretimi başarısız olan tasarım; analiz sayfasından yeniden üretebilirsin">
            {failed} başarısız
          </Link>
        )}
        <span className="inline-flex items-center gap-1 text-[10px] text-gray-400"><Clock className="h-3 w-3" />{new Date(a.createdAt).toLocaleDateString("tr-TR")}</span>
        <button onClick={onDelete} title="Analizi sil" className="mt-1 rounded p-1 text-gray-300 hover:bg-red-50 hover:text-red-500">
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

// ==== Toplu tasarim modali ====
function BulkDesignModal({ count, analysisIds, onClose, onDone }: {
  count: number; analysisIds: string[]; onClose: () => void; onDone: (msg: string) => void;
}) {
  const { models, model: effectiveModel, setModel } = useDesignModel("erc:model:bulk-design");
  // IP sIkI modlu magazada Yakin/Benzer yok
  const ipStrict = !!useShopVertical().shop?.ipStrict;
  const [fidelity, setFidelity] = useFidelity(ipStrict);
  const FIDELITY_OPTIONS = fidelityOptionsFor(ipStrict);
  const [perAnalysis, setPerAnalysis] = useState(1);

  const genM = useMutation({
    mutationFn: () => apiClient.bulkGenerateDesigns({ analysisIds, model: effectiveModel, fidelity, count: perAnalysis }),
    onSuccess: (r) => onDone(`${r.created} tasarım kuyruğa alındı — arka planda üretiliyor. Tasarımlar sayfasında görünecek.`),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            <Sparkles className="h-5 w-5 text-indigo-600" /> Toplu Tasarım Oluştur
          </h3>
          <button onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100"><X className="h-4 w-4" /></button>
        </div>
        <p className="mb-4 text-sm text-gray-500">
          <b>{count}</b> analiz için tasarım üretilecek. Her analiz <b>kendi verisiyle</b> (niş, tema, stil, motif, renk) prompt oluşturur.
        </p>

        <label className="mb-1 block text-xs font-medium text-gray-600">AI Modeli</label>
        <select value={effectiveModel} onChange={(e) => setModel(e.target.value)}
          className="mb-4 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200">
          {models.map((m) => <option key={m.id} value={m.id}>{modelLabel(m)}</option>)}
        </select>

        <label className="mb-1 block text-xs font-medium text-gray-600">İlham seviyesi</label>
        <div className="mb-1 grid grid-cols-5 gap-1.5">
          {FIDELITY_OPTIONS.map((f) => (
            <button key={f.key} onClick={() => setFidelity(f.key)} title={f.hint}
              className={`rounded-lg border px-1 py-2 text-xs font-medium transition-colors ${fidelity === f.key ? "border-indigo-500 bg-indigo-50 text-indigo-700" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}>
              {f.label}
            </button>
          ))}
        </div>
        <p className="mb-4 text-[11px] text-gray-400">{FIDELITY_OPTIONS.find((f) => f.key === fidelity)?.hint}</p>

        <label className="mb-1 block text-xs font-medium text-gray-600">Analiz başına adet</label>
        <div className="mb-5 flex gap-2">
          {[1, 2, 3, 4].map((n) => (
            <button key={n} onClick={() => setPerAnalysis(n)}
              className={`h-9 w-9 rounded-lg border text-sm font-medium transition-colors ${perAnalysis === n ? "border-indigo-500 bg-indigo-50 text-indigo-700" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}>
              {n}
            </button>
          ))}
          <span className="ml-auto self-center text-xs text-gray-400">Toplam ~{count * perAnalysis} tasarım</span>
        </div>

        {genM.isError && (
          <p className="mb-3 text-xs text-red-600">{(genM.error as Error)?.message || "Bir hata oluştu"}</p>
        )}

        <div className="flex gap-2">
          <button onClick={() => genM.mutate()} disabled={genM.isPending || !effectiveModel}
            className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
            {genM.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {genM.isPending ? "Kuyruğa alınıyor…" : "Oluştur"}
          </button>
          <button onClick={onClose} className="rounded-lg border border-gray-200 px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-50">İptal</button>
        </div>
      </div>
    </div>
  );
}
